import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HomeLotCard } from './components/HomeLotCard';
import { LiveLotCard } from './components/LiveLotCard';
import { LotHistoryList } from './components/LotHistoryList';
import { InfoIcon, LotsIcon, ProfileIcon, WalletIcon } from './components/NavIcons';
import { RecentWinCard } from './components/RecentWinCard';
import { SectionHeader } from './components/SectionHeader';
import { StartingSoonCard } from './components/StartingSoonCard';
import type { Lot } from './data/mockLots';
import { HISTORY_PAGE_SIZE } from './hooks/useHistory';
import { liveLotClosed, useClosedLots, useLots } from './hooks/useLots';
import { useOnlinePresence } from './hooks/useOnlinePresence';
import { useSession } from './hooks/useSession';
import { ApiError } from './lib/apiClient';
import { resolveStableHero } from './lib/heroStability';
import { historyView } from './lib/historyView';
import { buildAppDeeplink, openTgLink, readStartLotId } from './lib/tg';
import { LotDetail } from './pages/LotDetail';
import { Profile } from './pages/Profile';
import { Wallet } from './pages/Wallet';

type Tab = 'lots' | 'profile' | 'wallet';
// Both list-bearing views (home recent-wins, profile history) keep their reveal
// depth + scroll offset keyed here, lifted into App so they survive a drill-down
// into a lot (App stays mounted while the views unmount) and reset on a section
// change.
type ViewKey = 'home' | 'profile';

const RECENT_WINS_INITIAL = 5;
const RECENT_WINS_STEP = 10;
// Profile reveals one history page per click; step = page size so a network page
// is pulled only when the slice outruns the cache.
const PROFILE_HISTORY_STEP = HISTORY_PAGE_SIZE;

const SHOWN_INITIAL: Record<ViewKey, number> = {
  home: RECENT_WINS_INITIAL,
  profile: HISTORY_PAGE_SIZE,
};

// Only 'lots' and 'profile' are list-bearing; any future stub tab (wallet/info)
// resolves to the home view rather than silently inventing a key.
const viewKeyForTab = (t: Tab): ViewKey => (t === 'profile' ? 'profile' : 'home');

function readLotFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('lot');
}

// Friends opening a shared link land with `start_param=lot_<id>` instead of
// `?lot=<id>`. Promote it into the URL once so back/forward and refresh
// behave like a regular deeplink, then forget it.
function readInitialLotId(): string | null {
  const fromUrl = readLotFromUrl();
  if (fromUrl) return fromUrl;
  const fromStartParam = readStartLotId();
  if (fromStartParam) {
    const url = new URL(window.location.href);
    url.searchParams.set('lot', fromStartParam);
    window.history.replaceState({ lot: fromStartParam }, '', url);
    return fromStartParam;
  }
  return null;
}

function writeLotToUrl(id: string | null) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (id) url.searchParams.set('lot', id);
  else url.searchParams.delete('lot');
  window.history.pushState({ lot: id }, '', url);
}

export function App() {
  const [selectedId, setSelectedIdRaw] = useState<string | null>(() => readInitialLotId());
  // Active bottom-nav tab. selectedId always wins over the tab (a selected lot
  // renders LotDetail full-screen); the tab is kept in state so returning from a
  // lot lands back on whichever tab was active. Not URL-synced — only ?lot= is.
  const [tab, setTab] = useState<Tab>('lots');
  const lotsQuery = useLots(selectedId == null && tab === 'lots');
  const recentWinsQuery = useClosedLots();
  const queryClient = useQueryClient();
  // App-wide presence WS — kept alive across lot navigation (App stays mounted
  // when LotDetail renders), so the count reflects the whole session, not a lot.
  const { count: onlineCount } = useOnlinePresence();
  // Reactive: useSession re-renders on adjustBidBalance() after each bid, so the
  // header count stays in sync without its own fetch.
  const bidBalance = useSession()?.user.bid_balance ?? 0;

  // Per-view scroll position, captured when leaving for a lot so we can land back
  // where the user was instead of at the top (LotDetail replaces the view's
  // content, so the window scroll would otherwise be lost).
  const scrollRef = useRef<Record<ViewKey, number>>({ home: 0, profile: 0 });
  const viewKey = viewKeyForTab(tab);

  const setSelectedId = useCallback(
    (id: string | null) => {
      if (id != null) scrollRef.current[viewKey] = window.scrollY;
      setSelectedIdRaw(id);
      writeLotToUrl(id);
    },
    [viewKey],
  );

  useEffect(() => {
    const onPop = () => {
      if (selectedId == null) scrollRef.current[viewKey] = window.scrollY;
      setSelectedIdRaw(readLotFromUrl());
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [selectedId, viewKey]);

  // Restore the remembered scroll for whichever list view is showing; a lot
  // detail always starts at the top. A tab switch zeroes the incoming view's
  // offset (switchTab, below), so returning to a section lands at the top — only
  // a drill-down into a lot restores the prior position. Layout effect so it
  // lands before paint (no flash of the top of the list).
  useLayoutEffect(() => {
    window.scrollTo(0, selectedId == null ? scrollRef.current[viewKey] : 0);
  }, [selectedId, viewKey]);

  const buckets = lotsQuery.data;
  const allLots = useMemo<Lot[]>(
    () => (buckets ? [...buckets.live, ...buckets.scheduled] : []),
    [buckets],
  );
  // Full loaded set (up to the server cap); the home screen reveals a growing
  // slice of it via `shownCount`, while `closedTotal` drives the section count.
  const recentWins = useMemo<Lot[]>(() => recentWinsQuery.data?.lots ?? [], [recentWinsQuery.data]);
  const closedTotal = recentWinsQuery.data?.total ?? 0;
  const [shownCount, setShownCount] = useState<Record<ViewKey, number>>(SHOWN_INITIAL);

  // Switching top-level sections is a context change, not a drill-down: the
  // incoming section should land fresh — history collapsed and scrolled to top —
  // unlike coming back from a lot detail, which restores the prior position.
  const switchTab = useCallback(
    (next: Tab) => {
      if (next !== tab) {
        const key = viewKeyForTab(next);
        setShownCount((s) => ({ ...s, [key]: SHOWN_INITIAL[key] }));
        scrollRef.current[key] = 0;
      }
      setTab(next);
    },
    [tab],
  );

  // Closed lots have no poll of their own. The home screen isn't on any closing
  // lot's WS (that lives in LotDetail), so its only cue that a lot closed is the
  // live bucket shrinking on the 1s poll — refresh Recent wins off that edge.
  // react-query's structural sharing keeps `live` referentially stable between
  // unchanged polls, so this effect only re-runs when the set actually changes.
  const liveIds = useMemo(() => buckets?.live.map((l) => l.id) ?? null, [buckets]);
  const prevLiveIdsRef = useRef<string[] | null>(null);
  useEffect(() => {
    if (liveIds == null) return;
    const prev = prevLiveIdsRef.current;
    prevLiveIdsRef.current = liveIds;
    if (prev != null && liveLotClosed(prev, liveIds)) {
      queryClient.invalidateQueries({ queryKey: ['lots', 'closed'] });
    }
  }, [liveIds, queryClient]);

  const initialLot = useMemo(
    () => (selectedId ? [...allLots, ...recentWins].find((l) => l.id === selectedId) : undefined),
    [selectedId, allLots, recentWins],
  );

  const live = buckets?.live ?? [];
  const scheduled = buckets?.scheduled ?? [];
  // Hero is chosen server-side (selectHero), but its "liveliest" tier flips every
  // poll under active bidding. Pin the current hero while it's live so the screen
  // doesn't reshuffle; adopt a new server pick only once the current one closes.
  const stickyHeroIdRef = useRef<string | null>(null);
  const { featured, stickyId: heroStickyId } = resolveStableHero(
    stickyHeroIdRef.current,
    buckets?.hero ?? null,
    live,
  );
  useEffect(() => {
    stickyHeroIdRef.current = heroStickyId;
  }, [heroStickyId]);
  const otherLive = featured ? live.filter((l) => l.id !== featured.id) : live;
  const startingSoon = scheduled.filter(
    (l): l is Lot & { startsAt: number } => l.startsAt != null && l.id !== featured?.id,
  );
  // When the hero is a last-win lot it also lives in recentWins — drop it there,
  // then take the revealed slice. Filtering before the slice keeps the visible
  // count exact (the old code sliced first, so a hero collision could leave 4).
  const recentWinsFiltered = featured ? recentWins.filter((w) => w.id !== featured.id) : recentWins;
  // Home reveals from the already-loaded capped set only (no further fetch), so
  // hasNextPage is always false — the same decision helper the profile uses.
  const homeHistory = historyView({
    shownCount: shownCount.home,
    loadedCount: recentWinsFiltered.length,
    total: closedTotal,
    hasNextPage: false,
  });
  const recentWinsShown = recentWinsFiltered.slice(0, homeHistory.visibleCount);

  if (selectedId) {
    return (
      <LotDetail lotId={selectedId} initialLot={initialLot} onBack={() => setSelectedId(null)} />
    );
  }

  return (
    <div className="min-h-dvh mx-auto max-w-[480px] relative">
      <header className="sticky top-0 z-40 bg-bg/85 backdrop-blur-md border-b border-edge">
        <div className="px-4 h-12 flex items-center justify-between">
          <span className="font-display font-bold text-[18px] tracking-tight text-fg">
            skin<span className="text-primary">step</span>
            <span className="text-accent">_</span>
          </span>
          {onlineCount != null && (
            <span className="flex items-center gap-1.5 font-mono text-[10px] tracking-wider text-fg-muted">
              <span className="w-1.5 h-1.5 rounded-full bg-success anim-glow" />
              <span className="text-fg font-semibold">{onlineCount.toLocaleString('en-US')}</span>
              <span>online</span>
            </span>
          )}
          <button
            type="button"
            className="font-mono text-[11px] tracking-wider text-fg flex items-center gap-1.5 border border-primary/60 px-2.5 py-1 hover:border-primary transition-colors"
          >
            <span className="font-semibold">{bidBalance.toLocaleString('en-US')}</span>
            <span className="text-fg-muted">bids</span>
            <span className="text-primary ml-1">+</span>
          </button>
        </div>
      </header>

      {tab === 'profile' ? (
        <Profile
          onSelectLot={setSelectedId}
          shownCount={shownCount.profile}
          onShowMore={() =>
            setShownCount((s) => ({ ...s, profile: s.profile + PROFILE_HISTORY_STEP }))
          }
        />
      ) : tab === 'wallet' ? (
        <Wallet />
      ) : (
        <main className="pb-20">
          {lotsQuery.isPending && (
            <div className="px-4 pt-10 text-center">
              <p className="font-mono text-[11px] tracking-[0.25em] text-fg-muted">LOADING LOTS…</p>
            </div>
          )}

          {lotsQuery.isError &&
            (lotsQuery.error instanceof ApiError && lotsQuery.error.status === 401 ? (
              <div className="mx-3 mt-6 border border-edge bg-bg-elev/40 px-4 py-6 text-center">
                <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">
                  SESSION EXPIRED
                </p>
                <p className="text-[13px] text-fg-muted mt-2">
                  Your Telegram session timed out. Reopen SkinStep to pick up where you left off.
                </p>
                <button
                  type="button"
                  onClick={() => openTgLink(buildAppDeeplink())}
                  className="mt-3 font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10"
                >
                  REOPEN
                </button>
              </div>
            ) : (
              <div className="mx-3 mt-6 border border-danger/40 bg-danger/5 px-4 py-6 text-center">
                <p className="font-mono text-[10px] tracking-[0.25em] text-danger">
                  FAILED TO LOAD
                </p>
                <p className="text-[13px] text-fg-muted mt-2">
                  Could not reach the auction service.
                </p>
                <button
                  type="button"
                  onClick={() => lotsQuery.refetch()}
                  className="mt-3 font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10"
                >
                  RETRY
                </button>
              </div>
            ))}

          {!lotsQuery.isPending && !lotsQuery.isError && !featured && (
            <div className="mx-3 mt-8 border border-dashed border-edge px-4 py-10 text-center bg-bg-elev/30">
              <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">
                NO ACTIVE LOTS
              </p>
              <p className="text-[13px] text-fg-muted mt-2">
                The next batch is being prepared. Check back in a few minutes.
              </p>
            </div>
          )}

          {featured && (
            <div className="px-3 pt-4">
              <HomeLotCard lot={featured} onTap={() => setSelectedId(featured.id)} />
            </div>
          )}

          {otherLive.length > 0 && (
            <>
              <SectionHeader label="Live now" count={otherLive.length} accent="#ff4d4d" animate />
              <div className="px-3 flex flex-col gap-2">
                {otherLive.map((lot, i) => (
                  <LiveLotCard
                    key={lot.id}
                    lot={lot}
                    index={i}
                    onSelect={() => setSelectedId(lot.id)}
                  />
                ))}
              </div>
            </>
          )}

          {startingSoon.length > 0 && (
            <>
              <SectionHeader label="Starting soon" count={startingSoon.length} accent="#ffd93d" />
              <div className="snap-x-scroll no-scrollbar overflow-x-auto flex gap-2.5 px-3 scroll-px-3 pb-2">
                {startingSoon.map((lot, i) => (
                  <StartingSoonCard
                    key={lot.id}
                    lot={lot}
                    index={i}
                    onSelect={() => setSelectedId(lot.id)}
                  />
                ))}
              </div>
            </>
          )}

          {recentWinsShown.length > 0 && (
            <LotHistoryList
              label="Recent wins"
              count={closedTotal}
              accent="#00ff94"
              items={recentWinsShown}
              renderItem={(w, i) => (
                <RecentWinCard lot={w} index={i} onSelect={() => setSelectedId(w.id)} />
              )}
              canShowMore={homeHistory.canShowMore}
              showMoreCount={Math.min(
                RECENT_WINS_STEP,
                recentWinsFiltered.length - homeHistory.visibleCount,
              )}
              onShowMore={() => setShownCount((s) => ({ ...s, home: s.home + RECENT_WINS_STEP }))}
            />
          )}
        </main>
      )}

      <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-40 border-t border-edge bg-bg-deep/95 backdrop-blur-md">
        <div className="grid grid-cols-4 h-14">
          {[
            { k: 'Lots', Icon: LotsIcon, target: 'lots' as Tab },
            { k: 'Profile', Icon: ProfileIcon, target: 'profile' as Tab },
            { k: 'Wallet', Icon: WalletIcon, target: 'wallet' as Tab },
            { k: 'Info', Icon: InfoIcon, target: null },
          ].map((t) => {
            const target = t.target;
            const active = target != null && tab === target;
            return (
              <button
                key={t.k}
                type="button"
                onClick={target ? () => switchTab(target) : undefined}
                className={`flex flex-col items-center justify-center gap-1 text-[10px] tracking-wide transition-colors ${
                  active ? 'text-primary' : 'text-fg-muted hover:text-fg'
                }`}
              >
                <t.Icon className="w-[18px] h-[18px]" />
                <span>{t.k}</span>
                {active && <span className="w-5 h-px bg-primary mt-0.5" />}
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
